import { useEffect, useState } from "react";

import "./share-window.css";

// SH-5 — the Launch OS share window (Specs/Launch OS Sharing — PRD (Nov 3, 2026).md). One window
// for anything that can be shared: pick teammates, give each Can comment or Can view, or share
// with everyone on the team; remove anyone; and the no-sign-in link as its own section. Each
// change saves right away. The thing being shared supplies how to load and save its list, so a
// review, a media room or a file all use this same window.

export type ShareLevel = "comment" | "view";
// emailed/notEmailed come back from a save: who got the share email (SH-11)
export type ShareList = { team: ShareLevel | null; people: { email: string; level: ShareLevel }[]; emailed?: string[]; notEmailed?: string[] };
export type ShareTeammate = { name: string; email: string; initials: string; color: string | null; role?: string };
// levels: what can be picked (default both; Launch Media files have only view). hint: where sharees find it
export type SharePeople = { team: () => Promise<ShareTeammate[]>; load: () => Promise<ShareList>; save: (l: ShareList) => Promise<ShareList>; me?: string | null; roomUrl?: string; levels?: ShareLevel[]; hint?: string };
export type ShareLink = { load: () => Promise<string | null>; turn: (on: boolean) => Promise<string | null>; urlFor: (token: string) => string; blurb: string };

const LEVELS: { key: ShareLevel; label: string }[] = [{ key: "comment", label: "Can comment" }, { key: "view", label: "Can view" }];
const lc = (e: string | null | undefined) => String(e || "").trim().toLowerCase();

const firstName = (name: string) => (name.includes("@") ? name : name.split(" ")[0]);
const andList = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

export function ShareWindow({ title, people, link, onClose, onToast }: { title: string; people?: SharePeople | null; link?: ShareLink | null; onClose: () => void; onToast: (t: string) => void }) {
  const [team, setTeam] = useState<ShareTeammate[]>([]);
  const [list, setList] = useState<ShareList | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(!!link);
  const [notEmailed, setNotEmailed] = useState(""); // shown in the window too: a page's own notice can sit behind it
  const levels = people?.levels ? LEVELS.filter((l) => people.levels!.includes(l.key)) : LEVELS;

  useEffect(() => {
    let alive = true;
    if (people) Promise.all([people.team(), people.load()]).then(([t, l]) => { if (alive) { setTeam(t); setList(l); } }, () => alive && onToast("Couldn't load your team."));
    if (link) link.load().then((t) => { if (alive) { setToken(t); setLinkBusy(false); } }, () => { if (alive) { setLinkBusy(false); onToast("Couldn't check the share link."); } });
    return () => { alive = false; };
  }, []);

  // optimistic: show the change, save it, and put the old list back if the save fails
  const change = async (next: ShareList) => {
    if (!people || !list) return;
    const before = list;
    setList(next);
    try {
      const saved = await people.save(next);
      setList(saved);
      const missed = saved.notEmailed?.length ? `Couldn't email ${andList(saved.notEmailed.map((e) => firstName(nameOf(e).name)))}. Copy the link instead.` : "";
      setNotEmailed(missed);
      if (missed) onToast(missed);
    }
    catch (e) { setList(before); onToast("Couldn't save who it's shared with. Try again."); }
  };
  const nameOf = (email: string) => team.find((m) => lc(m.email) === lc(email)) || { name: email, email, initials: email.slice(0, 2).toUpperCase(), color: null };
  const shared = new Set((list?.people || []).map((p) => lc(p.email)));
  const addable = team.filter((m) => !shared.has(lc(m.email)) && lc(m.email) !== lc(people?.me));

  const turnLink = async (on: boolean) => {
    if (!link) return;
    setLinkBusy(true);
    try {
      const t = await link.turn(on);
      setToken(t);
      if (t) navigator.clipboard?.writeText(link.urlFor(t)).then(() => onToast("Link copied"), () => {});
    } catch (e) { onToast("Couldn't change the share link."); }
    setLinkBusy(false);
  };
  const copy = (text: string, done: string) => { const p = navigator.clipboard?.writeText(text); if (p) p.then(() => onToast(done), () => onToast("Select the link to copy it")); else onToast("Select the link to copy it"); };

  return (
    <div className="shw-ovl" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="shw" role="dialog" aria-label={title}>
        <div className="shw-top"><b>{title}</b><button onClick={onClose} aria-label="Close">✕</button></div>

        {people && (
          <section className="shw-sec" aria-label="People on your team">
            <h3>People on your team</h3>
            {!list ? <p className="shw-hint">Loading…</p> : (
              <>
                <label className="shw-row shw-all">
                  <span className="shw-av all" aria-hidden>👥</span>
                  <span className="shw-who"><b>Everyone on the team</b><small>Includes people who join later</small></span>
                  <select aria-label="Everyone on the team" value={list.team || ""} onChange={(e) => change({ ...list, team: (e.target.value || null) as ShareLevel | null })}>
                    <option value="">No access</option>
                    {levels.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                  </select>
                </label>
                {list.people.map((p) => {
                  const m = nameOf(p.email);
                  return (
                    <div className="shw-row" key={p.email} data-testid={`share-${lc(p.email)}`}>
                      <span className="shw-av" style={m.color ? { background: m.color } : undefined}>{m.initials}</span>
                      <span className="shw-who"><b>{m.name}</b><small>{p.email}</small></span>
                      {levels.length > 1 ? (
                        <select aria-label={`${m.name}'s access`} value={p.level} onChange={(e) => change({ ...list, people: list.people.map((x) => (x.email === p.email ? { ...x, level: e.target.value as ShareLevel } : x)) })}>
                          {levels.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                        </select>
                      ) : <span className="shw-level">{levels[0].label}</span>}
                      <button className="shw-x" aria-label={`Remove ${m.name}`} title="Remove" onClick={() => change({ ...list, people: list.people.filter((x) => x.email !== p.email) })}>✕</button>
                    </div>
                  );
                })}
                {addable.length > 0 ? (
                  <select className="shw-add" aria-label="Add a teammate" value="" onChange={(e) => e.target.value && change({ ...list, people: [...list.people, { email: e.target.value, level: levels[0].key }] })}>
                    <option value="">+ Add a teammate…</option>
                    {addable.map((m) => <option key={m.email} value={m.email}>{m.name}{m.role ? ` · ${m.role}` : ""}</option>)}
                  </select>
                ) : <p className="shw-hint">{team.length > 1 ? "Everyone on your team is on the list." : "Nobody else is on your team yet."}</p>}
                {notEmailed && <p className="shw-hint shw-warn" role="alert">{notEmailed}</p>}
                <p className="shw-hint">{people.hint || <>They'll find it under <b>Shared with me</b> in their Reviews. Comments from everyone land on this same copy, each with its writer's name.</>}</p>
                {people.roomUrl && <button className="shw-ghost" onClick={() => copy(people.roomUrl!, "Link copied. Paste it in Slack.")}>Copy the link for teammates</button>}
              </>
            )}
          </section>
        )}

        {link && (
          <section className="shw-sec" aria-label="Anyone with the link">
            <h3>Anyone with the link</h3>
            <p className="shw-hint">{link.blurb}</p>
            {token ? (
              <>
                <div className="shw-url"><input readOnly value={link.urlFor(token)} onFocus={(e) => e.currentTarget.select()} aria-label="Share link" /><button className="shw-primary" onClick={() => copy(link.urlFor(token), "Link copied")}>Copy</button></div>
                <button className="shw-off" disabled={linkBusy} onClick={() => turnLink(false)}>Turn the link off</button>
              </>
            ) : <button className="shw-primary" disabled={linkBusy} onClick={() => turnLink(true)}>{linkBusy ? "One moment…" : "Turn on the link and copy it"}</button>}
          </section>
        )}
      </div>
    </div>
  );
}
